import i18n from "../i18n";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatNumber } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Fish, Plus, Pencil, Trash2, Droplet, AlertTriangle, CheckCircle2, Thermometer,
} from "lucide-react";
import { toast } from "sonner";
import { farmDateIso } from "@/lib/farmTime";

const SPECIES_PRESETS = [
    "Tilapia", "Milkfish (Bangus)", "Catfish (Hito)", "Shrimp (Sugpo)",
    "Carp", "Mudfish (Dalag)", "Grouper (Lapu-Lapu)", "Prawn (Ulang)",
    "Oyster", "Seaweed", "Other",
];

const POND_STATUSES = ["active", "stocked", "harvested", "drained", "maintenance"];

const METRICS = [
    { key: "ph", label: "pH", step: "0.1", icon: Droplet },
    { key: "dissolved_oxygen", label: "DO (mg/L)", step: "0.1", icon: Droplet },
    { key: "temperature", label: "Temp (°C)", step: "0.1", icon: Thermometer },
    { key: "salinity", label: "Salinity (ppt)", step: "0.1", icon: Droplet },
    { key: "ammonia", label: "NH₃ (mg/L)", step: "0.01", icon: Droplet },
    { key: "nitrite", label: "NO₂ (mg/L)", step: "0.01", icon: Droplet },
    { key: "turbidity", label: "Turbidity (cm)", step: "1", icon: Droplet },
];

const FLAG_COLOR = {
    ok: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/40",
    warn: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/40",
    critical: "bg-destructive/10 text-destructive border-destructive/40",
    unknown: "bg-secondary text-muted-foreground border-border",
};

export default function AquaculturePage() {
    const [summary, setSummary] = useState(null);
    const [ponds, setPonds] = useState([]);
    const [logs, setLogs] = useState([]);
    const [dlg, setDlg] = useState(null);

    const load = useCallback(async () => {
        try {
            const [s, p, l] = await Promise.all([
                api.get("/aquaculture/summary").then(r => r.data),
                api.get("/ponds").then(r => r.data),
                api.get("/water-quality-logs").then(r => r.data),
            ]);
            setSummary(s); setPonds(p || []); setLogs(l || []);
        } catch (err) { toast.error("Failed to load aquaculture"); }
    }, []);
    // Silence dev-only ResizeObserver overlay from CRA overlay iframe (Recharts).
    // No effect in production build. Non-blocking, but the fullscreen dev overlay
    // occasionally intercepts clicks — this handler suppresses it.
    useEffect(() => {
        const handler = (e) => {
            if (typeof e?.message === "string" && e.message.includes("ResizeObserver loop")) {
                e.stopImmediatePropagation();
            }
        };
        window.addEventListener("error", handler);
        return () => window.removeEventListener("error", handler);
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async (path, body) => {
        try {
            if (body.id) { const { id, ...rest } = body; await api.put(`/${path}/${id}`, rest); }
            else await api.post(`/${path}`, body);
            toast.success("Saved"); setDlg(null); load();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed"); }
    };

    const del = async (path, id, label) => {
        if (!window.confirm(`Delete ${label}?`)) return;
        try { await api.delete(`/${path}/${id}`); toast.success("Deleted"); load(); }
        catch (err) { toast.error("Delete failed"); }
    };

    const pondMap = useMemo(() => Object.fromEntries(ponds.map(p => [p.id, p])), [ponds]);

    if (!summary) return <div className="p-6 text-muted-foreground">{i18n.t("final.33ce417454bf")}</div>;
    const k = summary.kpis;
    const ranges = summary.reference_ranges || {};

    return (
        <div className="space-y-6" data-testid="aquaculture-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="tiny-label flex items-center gap-2"><Fish className="w-4 h-4" />{i18n.t("final.1efd3e280145")}</div>
                    <h1 className="text-3xl font-bold mt-1">{i18n.t("final.5b62ea1bf903")}</h1>
                    <p className="text-sm text-muted-foreground">{i18n.t("final.4c25b4dd9300")}</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setDlg({ kind: "log", data: {} })} data-testid="new-log-btn">
                        <Droplet className="w-4 h-4 mr-1" />{i18n.t("final.91262062ba6b")}
                    </Button>
                    <Button onClick={() => setDlg({ kind: "pond", data: {} })} data-testid="new-pond-btn">
                        <Plus className="w-4 h-4 mr-1" />{i18n.t("final.f16f60e54c46")}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                <Kpi label={i18n.t("final.4be4414aa252")} value={k.total_ponds} testid="aqua-kpi-total" />
                <Kpi label={i18n.t("final.a733b809d2f1")} value={k.active_ponds} testid="aqua-kpi-active" tone="primary" />
                <Kpi label={i18n.t("final.dc6a02db7a5b")} value={k.total_stocked} testid="aqua-kpi-stocked" />
                <Kpi label={i18n.t("final.126dd3b70a5c")} value={k.logs_count} testid="aqua-kpi-logs" />
                <Kpi label={i18n.t("final.80ea156e1b83")} value={k.healthy_water_quality} tone="green" testid="aqua-kpi-healthy" />
                <Kpi label={i18n.t("final.e9c45563358e")} value={k.warning_water_quality} tone="amber" testid="aqua-kpi-warn" />
                <Kpi label={i18n.t("final.04b7b26c8ca2")} value={k.critical_water_quality} tone="red" testid="aqua-kpi-critical" />
            </div>

            <Tabs defaultValue="ponds">
                <TabsList data-testid="aqua-tabs">
                    <TabsTrigger value="ponds" data-testid="aqua-tab-ponds"><Fish className="w-4 h-4 mr-1" />{i18n.t("final.48bc81639dbc")}</TabsTrigger>
                    <TabsTrigger value="logs" data-testid="aqua-tab-logs"><Droplet className="w-4 h-4 mr-1" />{i18n.t("final.4a3c315b5cb3")}</TabsTrigger>
                    <TabsTrigger value="reference" data-testid="aqua-tab-reference">{i18n.t("final.b1da58baf669")}</TabsTrigger>
                </TabsList>

                <TabsContent value="ponds" className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {ponds.length === 0 && (
                            <Card className="p-8 col-span-full text-center text-muted-foreground">
                                {i18n.t("final.3a670a7af036")} <Button variant="link" onClick={() => setDlg({ kind: "pond", data: {} })}>{i18n.t("final.b218a79feffb")}</Button>
                            </Card>
                        )}
                        {ponds.map((p) => {
                            const latest = summary.latest_per_pond?.[p.id];
                            return (
                                <Card key={p.id} className="p-5 space-y-3" data-testid={`pond-card-${p.id}`}>
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <div className="font-bold text-lg">{p.name}</div>
                                            <div className="text-xs text-muted-foreground">{p.species || "—"} · {p.size_sqm || 0} {i18n.t("final.6f6f0f6a0fb3")}</div>
                                        </div>
                                        <Badge variant={p.status === "active" ? "default" : "secondary"} className="capitalize text-xs">{p.status || "active"}</Badge>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <Stat label={i18n.t("final.09a36db7c9f2")} value={formatNumber(p.stocked_count || 0)} />
                                        <Stat label={i18n.t("final.e7278d717608")} value={p.stocked_date || "—"} />
                                        {p.target_harvest_date && <Stat label={i18n.t("final.7e4edd862a9d")} value={p.target_harvest_date} />}
                                        {p.water_source && <Stat label={i18n.t("final.e52a04d0c8ce")} value={p.water_source} />}
                                    </div>
                                    {latest && (
                                        <div className="border-t pt-2">
                                            <div className="tiny-label mb-1.5">{i18n.t("final.b6531fded9a0")} {latest.date}</div>
                                            <div className="grid grid-cols-4 gap-1">
                                                {METRICS.slice(0, 4).map(m => {
                                                    const flag = latest.flags?.[m.key] || "unknown";
                                                    const v = latest[m.key];
                                                    return (
                                                        <div
                                                            key={m.key}
                                                            className={`text-center rounded border px-1 py-1 text-[10px] ${FLAG_COLOR[flag]}`}
                                                            title={`${m.label}: ${v ?? "—"}`}
                                                        >
                                                            <div className="font-mono text-xs font-bold">{v ?? "—"}</div>
                                                            <div className="uppercase font-semibold">{m.label.split(" ")[0]}</div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                    <div className="flex justify-end gap-1 pt-2 border-t">
                                        <Button size="sm" variant="ghost" onClick={() => setDlg({ kind: "log", data: { pond_id: p.id } })} data-testid={`log-water-${p.id}`}>
                                            <Droplet className="w-3.5 h-3.5" />
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={() => setDlg({ kind: "pond", data: p })} data-testid={`edit-pond-${p.id}`}>
                                            <Pencil className="w-3.5 h-3.5" />
                                        </Button>
                                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del("ponds/cascade", p.id, p.name)} data-testid={`delete-pond-${p.id}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                </TabsContent>

                <TabsContent value="logs" className="space-y-3">
                    <Card className="overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/50"><tr className="text-left">
                                <Th>{i18n.t("final.eb9a4bc1c0c1")}</Th><Th>{i18n.t("final.64f5d600a1cc")}</Th>{METRICS.map(m => <Th key={m.key}>{m.label}</Th>)}<Th></Th>
                            </tr></thead>
                            <tbody>
                                {logs.length === 0 && <tr><td colSpan={METRICS.length + 3} className="text-center text-muted-foreground py-6">{i18n.t("final.f01f5d7ee661")}</td></tr>}
                                {logs.slice().sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((l) => {
                                    const flags = _flagsFor(l, ranges);
                                    return (
                                        <tr key={l.id} className="border-t" data-testid={`log-row-${l.id}`}>
                                            <td className="p-2 text-xs">{l.date || "—"}</td>
                                            <td className="p-2 font-medium">{pondMap[l.pond_id]?.name || "—"}</td>
                                            {METRICS.map(m => {
                                                const v = l[m.key];
                                                const flag = flags[m.key];
                                                return (
                                                    <td key={m.key} className="p-2">
                                                        {v != null && v !== "" ? (
                                                            <span className={`inline-block px-1.5 py-0.5 rounded font-mono text-xs border ${FLAG_COLOR[flag]}`}>{v}</span>
                                                        ) : "—"}
                                                    </td>
                                                );
                                            })}
                                            <td className="p-2 text-right whitespace-nowrap">
                                                <Button size="sm" variant="ghost" onClick={() => setDlg({ kind: "log", data: l })}><Pencil className="w-3.5 h-3.5" /></Button>
                                                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del("water-quality-logs", l.id, "log")}><Trash2 className="w-3.5 h-3.5" /></Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>

                <TabsContent value="reference" className="space-y-3">
                    <Card className="p-5">
                        <div className="mb-4">
                            <h3 className="font-semibold flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" />{i18n.t("final.ecc3fb27c580")}</h3>
                            <p className="text-xs text-muted-foreground mt-1">{i18n.t("final.161e50dbc720")}</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {METRICS.map(m => {
                                const r = ranges[m.key];
                                return (
                                    <Card key={m.key} className="p-3 bg-secondary/40">
                                        <div className="tiny-label">{m.label}</div>
                                        <div className="text-2xl font-mono font-bold mt-1 text-primary">
                                            {r ? `${r.min} – ${r.max}` : "—"}
                                        </div>
                                        {r?.unit && <div className="text-xs text-muted-foreground">{r.unit}</div>}
                                    </Card>
                                );
                            })}
                        </div>
                    </Card>
                </TabsContent>
            </Tabs>

            {dlg?.kind === "pond" && <PondDialog initial={dlg.data} onSave={(b) => save("ponds", b)} onClose={() => setDlg(null)} />}
            {dlg?.kind === "log" && <LogDialog initial={dlg.data} ponds={ponds} ranges={ranges} onSave={(b) => save("water-quality-logs", b)} onClose={() => setDlg(null)} />}
        </div>
    );
}

// Client-side mirror of the backend flag logic — for real-time colouring in the log table
function _flagsFor(log, ranges) {
    const out = {};
    for (const [key, r] of Object.entries(ranges || {})) {
        const v = log[key];
        if (v == null || v === "" || v === undefined) { out[key] = "unknown"; continue; }
        const n = Number(v);
        if (Number.isNaN(n)) { out[key] = "unknown"; continue; }
        if (n >= r.min && n <= r.max) { out[key] = "ok"; continue; }
        const span = (r.max - r.min) || 1;
        const dev = Math.min(Math.abs(n - r.min), Math.abs(n - r.max)) / span;
        out[key] = dev > 0.20 ? "critical" : "warn";
    }
    return out;
}

function Kpi({ label, value, testid, tone = "slate" }) {
    const cls = {
        primary: "bg-primary/10 border-primary/40 text-primary",
        green: "bg-emerald-500/10 border-emerald-500/40 text-emerald-700 dark:text-emerald-400",
        amber: "bg-amber-500/10 border-amber-500/40 text-amber-700 dark:text-amber-400",
        red: "bg-destructive/10 border-destructive/40 text-destructive",
    }[tone] || "bg-secondary border-border";
    return (
        <Card className={`p-3 border ${cls}`} data-testid={testid}>
            <div className="tiny-label">{label}</div>
            <div className="text-2xl font-bold mt-1">{formatNumber(value || 0)}</div>
        </Card>
    );
}
function Th({ children }) { return <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold">{children}</th>; }
function Stat({ label, value }) { return <div><div className="text-[10px] uppercase text-muted-foreground">{label}</div><div className="font-mono">{value}</div></div>; }
function Field({ label, children }) { return <div><div className="text-xs text-muted-foreground mb-1">{label}</div>{children}</div>; }

function PondDialog({ initial, onSave, onClose }) {
    const [f, setF] = useState({
        name: "", species: "Tilapia", size_sqm: "", stocked_count: "",
        stocked_date: farmDateIso(),
        target_harvest_date: "", water_source: "", status: "active", notes: "", ...initial,
    });
    const set = (k, v) => setF({ ...f, [k]: v });
    return (
        <Dialog open onOpenChange={onClose}><DialogContent className="max-w-md" data-testid="pond-dialog">
            <DialogHeader><DialogTitle>{f.id ? "Edit Pond" : "New Pond"}</DialogTitle><DialogDescription>{i18n.t("final.1e49abec1181")}</DialogDescription></DialogHeader>
            <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                    <Field label={i18n.t("final.57162c35dea4")}><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="pond-name-input" placeholder={i18n.t("final.a5b943a2e873")} /></Field>
                    <Field label={i18n.t("final.fa16fa0d667b")}>
                        <Select value={f.species} onValueChange={(v) => set("species", v)}>
                            <SelectTrigger data-testid="pond-species-select"><SelectValue /></SelectTrigger>
                            <SelectContent>{SPECIES_PRESETS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                    </Field>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Field label={i18n.t("final.a2a8b6d44e29")}><Input type="number" step="1" value={f.size_sqm} onChange={(e) => set("size_sqm", e.target.value)} /></Field>
                    <Field label={i18n.t("final.e40e6e6c4312")}><Input type="number" value={f.stocked_count} onChange={(e) => set("stocked_count", e.target.value)} data-testid="pond-stock-input" /></Field>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Field label={i18n.t("final.e7278d717608")}><Input type="date" value={f.stocked_date} onChange={(e) => set("stocked_date", e.target.value)} /></Field>
                    <Field label={i18n.t("final.404c5194e7ac")}><Input type="date" value={f.target_harvest_date} onChange={(e) => set("target_harvest_date", e.target.value)} /></Field>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Field label={i18n.t("final.e52a04d0c8ce")}><Input value={f.water_source} onChange={(e) => set("water_source", e.target.value)} placeholder={i18n.t("final.38ff36471160")} /></Field>
                    <Field label={i18n.t("final.bae7d5be7082")}>
                        <Select value={f.status} onValueChange={(v) => set("status", v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{POND_STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                        </Select>
                    </Field>
                </div>
                <Field label={i18n.t("final.70440046a3dc")}><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
            </div>
            <DialogFooter>
                <Button variant="outline" onClick={onClose}>{i18n.t("final.77dfd2135f4d")}</Button>
                <Button
                    onClick={() => f.name.trim() ? onSave({
                        ...f,
                        size_sqm: Number(f.size_sqm) || 0,
                        stocked_count: Number(f.stocked_count) || 0,
                    }) : toast.error("Name required")}
                    data-testid="save-pond-btn"
                >{i18n.t("final.efc007a393f6")}</Button>
            </DialogFooter>
        </DialogContent></Dialog>
    );
}

function LogDialog({ initial, ponds, ranges, onSave, onClose }) {
    const [f, setF] = useState({
        pond_id: "", date: farmDateIso(),
        ph: "", dissolved_oxygen: "", temperature: "",
        salinity: "", ammonia: "", nitrite: "", turbidity: "", notes: "", ...initial,
    });
    const set = (k, v) => setF({ ...f, [k]: v });
    const flags = _flagsFor(f, ranges);
    const anyCritical = Object.values(flags).some(x => x === "critical");
    return (
        <Dialog open onOpenChange={onClose}><DialogContent className="max-w-lg" data-testid="log-dialog">
            <DialogHeader>
                <DialogTitle>{f.id ? "Edit Reading" : "Log Water Quality"}</DialogTitle>
                <DialogDescription>{i18n.t("final.16f70fd60f05")}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                    <Field label={i18n.t("final.efac482c1b4d")}>
                        <Select value={f.pond_id} onValueChange={(v) => set("pond_id", v)}>
                            <SelectTrigger data-testid="log-pond-select"><SelectValue placeholder={i18n.t("final.c3b69e661eec")} /></SelectTrigger>
                            <SelectContent>{ponds.map(p => <SelectItem key={p.id} value={p.id}>{p.name} — {p.species}</SelectItem>)}</SelectContent>
                        </Select>
                    </Field>
                    <Field label={i18n.t("final.eb9a4bc1c0c1")}><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} /></Field>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {METRICS.map(m => {
                        const flag = flags[m.key];
                        return (
                            <Field key={m.key} label={m.label}>
                                <Input
                                    type="number" step={m.step} value={f[m.key]}
                                    onChange={(e) => set(m.key, e.target.value)}
                                    data-testid={`log-${m.key}-input`}
                                    className={
                                        flag === "critical" ? "border-destructive focus-visible:ring-destructive" :
                                        flag === "warn" ? "border-amber-500 focus-visible:ring-amber-500" :
                                        flag === "ok" ? "border-emerald-500" : ""
                                    }
                                />
                                {ranges[m.key] && <div className="text-[10px] text-muted-foreground mt-0.5">{ranges[m.key].min} – {ranges[m.key].max} {ranges[m.key].unit}</div>}
                            </Field>
                        );
                    })}
                </div>
                {anyCritical && (
                    <div className="flex items-start gap-2 p-2 rounded-lg bg-destructive/10 text-destructive text-xs" data-testid="log-critical-warning">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <div>{i18n.t("final.eefc13716b45")}</div>
                    </div>
                )}
                <Field label={i18n.t("final.70440046a3dc")}><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
            </div>
            <DialogFooter>
                <Button variant="outline" onClick={onClose}>{i18n.t("final.77dfd2135f4d")}</Button>
                <Button
                    onClick={() => f.pond_id
                        ? onSave(Object.fromEntries(Object.entries(f).map(([k, v]) => [k, METRICS.some(m => m.key === k) ? (v === "" ? null : Number(v)) : v])))
                        : toast.error("Pond required")}
                    data-testid="save-log-btn"
                >{i18n.t("final.cccb682b71cf")}</Button>
            </DialogFooter>
        </DialogContent></Dialog>
    );
}
